/**
 * Pastille glissante du choix segmenté (SegmentedControl) – premier usage de Motion, cas « complexe » : la pastille
 * suit l'option choisie (clic, flèches du clavier, valeur changée par l'écran), y compris quand les libellés changent
 * de largeur (langue, police chargée) ou que le groupe défile.
 *
 * Description du mouvement (5 informations, web/README.md › Mouvement) :
 *   - ce qui bouge : la pastille de l'option choisie (fond en verre dense et ombre de `.ag-seg-on`) ;
 *   - déclencheur : le choix d'une autre option ;
 *   - mouvement : glissement (position et taille) de l'ancienne option vers la nouvelle ;
 *   - durée : moyenne (--duration-medium), courbe d'entrée (--ease-enter) ;
 *   - mouvement réduit : la pastille se place directement sur la nouvelle option.
 *
 * DOM et classes du design inchangés : l'option choisie garde `ag-seg-on` (texte) ; la pastille est un `span`
 * décoratif (aria-hidden, hors clavier) placé avant les options, qui porte `ag-seg-on agx-seg-pastille` pour reprendre
 * exactement l'apparence du design (styles : core/mouvement.css). Clavier et lecteurs d'écran : Radix RadioGroup,
 * inchangés. Motion n'est pas dans le bundle principal : la pastille arrive avec son module (motion/pastille-motion.ts) ;
 * en attendant (ou si la mesure est impossible), l'option choisie garde son propre fond, comme avant.
 */
import React from 'react';
import { h } from './base';
import type { BoitePastille } from './motion/pastille-motion';

const PastilleMotion = React.lazy(() => import('./motion/pastille-motion'));

const OPTIONS = ':scope > [role="radio"]';

/** Position de l'option choisie dans le groupe (repère : le groupe, positionné ; coordonnées physiques). */
function mesurer(groupe: HTMLElement): BoitePastille | null {
  const choisie = groupe.querySelector<HTMLElement>(`${OPTIONS}[data-state="checked"]`);
  if (!choisie || !choisie.offsetWidth) return null;
  return { x: choisie.offsetLeft, y: choisie.offsetTop, largeur: choisie.offsetWidth, hauteur: choisie.offsetHeight };
}

const memeBoite = (a: BoitePastille | null, b: BoitePastille | null) =>
  a === b || (!!a && !!b && a.x === b.x && a.y === b.y && a.largeur === b.largeur && a.hauteur === b.hauteur);

/**
 * `ref` à poser sur le groupe (RadioGroup.Root) et `element` à rendre en premier enfant. La pastille est mesurée après
 * chaque rendu et quand le groupe ou une option change de taille.
 */
export function usePastilleGlissante(): { ref: React.RefObject<HTMLDivElement>; element: React.ReactNode } {
  const ref = React.useRef<HTMLDivElement>(null);
  const [boite, setBoite] = React.useState<BoitePastille | null>(null);

  const actualiser = React.useCallback(() => {
    const groupe = ref.current;
    if (!groupe) return;
    const nouvelle = mesurer(groupe);
    setBoite((ancienne) => (memeBoite(ancienne, nouvelle) ? ancienne : nouvelle));
  }, []);

  // Après chaque rendu (option choisie, libellés) : mesure avant l'affichage, la pastille ne saute jamais.
  React.useLayoutEffect(actualiser);

  // Changement de taille sans nouveau rendu (police chargée, page de nouveau visible, largeur du volet).
  React.useEffect(() => {
    const groupe = ref.current;
    if (!groupe || typeof ResizeObserver === 'undefined') return undefined;
    const observateur = new ResizeObserver(actualiser);
    observateur.observe(groupe);
    groupe.querySelectorAll(OPTIONS).forEach((o) => observateur.observe(o));
    return () => observateur.disconnect();
  }, [actualiser]);

  const element = boite ? h(React.Suspense, { fallback: null }, h(PastilleMotion, { boite })) : null;
  return { ref, element };
}
